import axios from 'axios';
import { useSnackbar } from 'notistack5';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useCalendarApi } from 'src/api/useCalendarApi';
import { useDispatch } from 'src/redux/store';
import { API_PATH } from '../../routes/generalPath';
import { CALENDAR_EDIT_TAB_VALUE, TabValue } from '../constants';
import { enqueteCalendarSlice } from '../store/enqueteCalendarSlice';
import { ReminderEntity } from './useReminderListGrid';
// カレンダーデータエンティティ
export interface CalendarInfoEntity {
  id: string;
  // 見出し（日本語）
  titleJpn: string;
  // 見出し（英語）
  titleEng: string;
  // 本文（日本語）
  contentJpn: string | null;
  // 本文（英語）
  contentEng: string | null;
  // 適用開始日
  openAt: string;
  // 適用終了日
  closeAt: string;
  // アンケート詳細ID
  surveyDetailId: string | null;
  // respondentTargetId
  respondentTargetId: string | undefined;
  // 回答ID
  answerId: string;
  // 作成者
  createdBy?: string;
  // 更新者
  updatedBy?: string | null;
  // 作成時間
  createdAt?: Date;
  // 更新時間
  updatedAt?: Date;
  // リマインドのメールアドレス
  reminderUserId: string;
  // リマインドデータ
  reminderListData: ReminderEntity[];
  // 削除リマインドIDリスト
  deleteRowIds: string[];
}
// カレンダー新規作成データ編集
export const initCalendarData: CalendarInfoEntity = {
  id: '',
  // 見出し（日本語）
  titleJpn: '',
  // 見出し（英語）
  titleEng: '',
  // 本文（日本語）
  contentJpn: '',
  // 本文（英語）
  contentEng: '',
  // 適用開始日
  openAt: '',
  // 適用終了日
  closeAt: '',
  // アンケート詳細ID
  surveyDetailId: '',
  // respondentTargetId
  respondentTargetId: '',
  // 回答ID
  answerId: '',
  // リマインドのメールアドレス
  reminderUserId: '',
  // リマインドデータ
  reminderListData: [],
  // 削除リマインドIDリスト
  deleteRowIds: [],
};

/**
 * コンプライアンス・レビュー カレンダー予定登録画面 API
 *
 * @returns
 */
const useCalendarLayout = () => {
  // Redux dispatch 関数を取得
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  //ローディング
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // カレンダー関連データ取得 API
  const enqCalendarListApi = useCalendarApi();
  const navigate = useNavigate();

  /**
   * コンプライアンス・レビュー カレンダー予定登録の初期化処理
   */
  const initializeStore = () => {
    //ローディング開始
    setIsLoading(true);
    // カレンダーデータクリア
    dispatch(enqueteCalendarSlice.actions.clearCalendarData());
    // リマインドデータクリア
    dispatch(enqueteCalendarSlice.actions.clearReminderListData());
    // 初期日本語タブ設定
    dispatch(enqueteCalendarSlice.actions.setTabValue(CALENDAR_EDIT_TAB_VALUE.JAPANESE as TabValue));
    //ローディング終了
    setIsLoading(false);
  };

  /**
   * カレンダーデータ取得
   */
  const getCalendarData = async (calendarlId: string) => {
    //ローディング開始
    setIsLoading(true);
    // カレンダーデータ取得
    const calendarData = await enqCalendarListApi.getCalendarData(calendarlId);
    if (calendarData === undefined) {
      return 1;
    }
    // カレンダーの初期化
    dispatch(enqueteCalendarSlice.actions.setCalendarData(calendarData));
    //ローディング終了
    setIsLoading(false);
    return calendarData;
  };

  /**
   * カレンダー情報とリマインド情報を保存する
   * @param calendarData カレンダーデータ
   * @param reminderListData リマインド明細データ
   * @returns
   */
  const onClickSaveData = async (calendarData: CalendarInfoEntity, reminderListData: ReminderEntity[]) => {
    try {
      //ローディング開始
      setIsLoading(true);
      // カレンダー情報とリマインド情報を保存する
      await enqCalendarListApi.saveCalendarData(calendarData, reminderListData);
      //ローディング終了
      setIsLoading(false);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // エラーメッセージ：カレンダー情報の更新に失敗しました(Failed to Update Calendar Data)
        enqueueSnackbar(t('CalendarCreate.message.failedUpdateCalendarData'), { variant: 'error' });
      } else {
        throw error;
      }
      return;
    }
    if (calendarData.id === undefined || calendarData.id === '') {
      // データクリア
      initializeStore();
    }
    // カレンダー管理画面へ遷移
    navigate(`${API_PATH.ENQUETE_CALENDAR}`);
  };

  return {
    //ローディング
    isLoading,
    // コンプライアンス・レビュー カレンダー予定登録の初期化処理
    initializeStore,
    // カレンダーデータ取得
    getCalendarData,
    // カレンダー情報とリマインド情報の保存
    onClickSaveData,
  };
};

export default useCalendarLayout;
